import * as React from 'react';
import './ZoomApp.css';
import PlusIcon from '../../assets/Icons/PlusIcon.svg?react';
import MinusIcon from '../../assets/Icons/MinusIcon.svg?react';
import {
  PORTABLE_ZOOM_ROOT_SELECTOR,
  portableZoomLimits,
  readStoredPortableZoom,
  resetDocumentZoom,
  writeStoredPortableZoom,
} from './zoomDocument';

type ZoomAppProps = {
  orientation?: 'vertical' | 'horizontal';
  compact?: boolean;
};

const ZoomApp = React.memo(({ orientation = 'vertical', compact = false }: ZoomAppProps) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const zoomValueRef = React.useRef(readStoredPortableZoom());

  const getZoomTarget = React.useCallback(() => {
    return rootRef.current?.closest(PORTABLE_ZOOM_ROOT_SELECTOR) as HTMLElement | null;
  }, []);

  const applyZoom = React.useCallback(() => {
    resetDocumentZoom();

    const zoomTarget = getZoomTarget();
    if (!zoomTarget) {
      return;
    }

    writeStoredPortableZoom(zoomValueRef.current);
    zoomTarget.style.setProperty('--portable-zoom', String(zoomValueRef.current / 100));
    zoomTarget.style.zoom = `${zoomValueRef.current}%`;
  }, [getZoomTarget]);

  React.useLayoutEffect(() => {
    applyZoom();

    return () => {
      const zoomTarget = getZoomTarget();
      if (zoomTarget) {
        zoomTarget.style.zoom = '';
      }
      resetDocumentZoom();
    };
  }, [applyZoom, getZoomTarget]);

  const handlePlusClick = () => {
    zoomValueRef.current = Math.min(
      portableZoomLimits.max,
      zoomValueRef.current + portableZoomLimits.step,
    );
    applyZoom();
  };

  const handleMinusClick = () => {
    zoomValueRef.current = Math.max(
      portableZoomLimits.min,
      zoomValueRef.current - portableZoomLimits.step,
    );
    applyZoom();
  };

  return (
    <div className={`ZoomApp${compact ? ' ZoomApp--compact' : ''}`} ref={rootRef}>
      <div
        className={`ZoomApp--Panel${orientation === 'horizontal' ? ' ZoomApp--Panel--horizontal' : ''}${compact ? ' ZoomApp--Panel--compact' : ''}`}
        role="group"
        aria-label="Масштаб"
      >
        <button
          type="button"
          className="ZoomApp--PanelIcon"
          aria-label="Увеличить"
          onClick={handlePlusClick}
        >
          <PlusIcon />
        </button>
        <button
          type="button"
          className="ZoomApp--PanelIcon"
          aria-label="Уменьшить"
          onClick={handleMinusClick}
        >
          <MinusIcon />
        </button>
      </div>
    </div>
  );
});

export default ZoomApp;
